import React, { useState, useEffect } from 'react';
import { Phone, Info, Car, Clock, Navigation, Plus, Check } from 'lucide-react';
import { companyInfo } from '../data.js';

export default function TaxaCalculator() {
  const [carSize, setCarSize] = useState('lille'); // 'lille' | 'stor'
  const [timeOfDay, setTimeOfDay] = useState('dag'); // 'dag' | 'nat'
  const [distance, setDistance] = useState(15); // km
  const [duration, setDuration] = useState(20); // minutes
  const [autoEstimateTime, setAutoEstimateTime] = useState(true);
  const [extras, setExtras] = useState({
    cykel: false,
    koerestol: false,
  });

  // Rates definition
  const rates = {
    lille: {
      dag: { start: 49.00, km: 12.30, min: 6.66 },
      nat: { start: 59.00, km: 13.74, min: 7.50 }
    },
    stor: {
      dag: { start: 69.00, km: 17.00, min: 6.66 },
      nat: { start: 79.00, km: 19.25, min: 7.50 }
    }
  };

  // Auto estimate time when distance changes
  useEffect(() => {
    if (autoEstimateTime) {
      // Estimate 1.2 minutes per km as a realistic average speed (50 km/h average including stops) - e.g. 10 km = 12 minutes
      const estimatedMinutes = Math.max(2, Math.round(distance * 1.2));
      setDuration(estimatedMinutes);
    }
  }, [distance, autoEstimateTime]);

  const currentRates = rates[carSize][timeOfDay];
  
  const startCost = currentRates.start;
  const kmCost = currentRates.km * distance;
  const timeCost = currentRates.min * duration;
  const extrasCost = (extras.cykel ? 30 : 0) + (extras.koerestol ? 100 : 0);
  
  const totalCost = startCost + kmCost + timeCost + extrasCost;

  const handleDistanceChange = (e) => {
    setDistance(Number(e.target.value));
  };

  const handleDurationChange = (e) => {
    setDuration(Number(e.target.value));
    setAutoEstimateTime(false); // Disable auto-estimate if user manually overrides
  };

  const toggleExtra = (key) => {
    setExtras(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden max-w-4xl mx-auto">
      <div className="grid md:grid-cols-12">
        {/* Left: Inputs */}
        <div className="p-6 sm:p-8 md:col-span-7 space-y-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Prisberegner</span>
            <h3 className="text-2xl font-bold text-slate-900 mt-1">Beregn din taxapris</h3>
            <p className="text-sm text-slate-500 mt-1">
              Få et hurtigt og realistisk estimat på din næste taxatur i Thy og på Mors.
            </p>
          </div>

          {/* Car Size Selection */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 block">Vælg vognstørrelse</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCarSize('lille')}
                className={`flex items-center justify-center gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
                  carSize === 'lille'
                    ? 'border-blue-600 bg-blue-50/50 text-blue-900'
                    : 'border-slate-100 hover:border-slate-200 text-slate-600'
                }`}
              >
                <Car className={`h-5 w-5 ${carSize === 'lille' ? 'text-blue-600' : 'text-slate-400'}`} />
                <div>
                  <div className="font-bold text-sm">Lillebil</div>
                  <div className="text-xs opacity-80">Op til 4 personer</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setCarSize('stor')}
                className={`flex items-center justify-center gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
                  carSize === 'stor'
                    ? 'border-blue-600 bg-blue-50/50 text-blue-900'
                    : 'border-slate-100 hover:border-slate-200 text-slate-600'
                }`}
              >
                <Car className={`h-6 w-6 ${carSize === 'stor' ? 'text-blue-600' : 'text-slate-400'}`} />
                <div>
                  <div className="font-bold text-sm">Storbil</div>
                  <div className="text-xs opacity-80">5 - 8 personer</div>
                </div>
              </button>
            </div>
          </div>

          {/* Time of Day Selection */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 block">Tidspunkt for kørsel</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setTimeOfDay('dag')}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border text-sm font-medium transition-all ${
                  timeOfDay === 'dag'
                    ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                    : 'bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100'
                }`}
              >
                <span>Dag (06:00 - 18:00)</span>
              </button>
              <button
                type="button"
                onClick={() => setTimeOfDay('nat')}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border text-sm font-medium transition-all ${
                  timeOfDay === 'nat'
                    ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                    : 'bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100'
                }`}
              >
                <span>Nat / Weekend</span>
              </button>
            </div>
          </div>

          {/* Distance Slider */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Navigation className="h-4 w-4 text-slate-400" />
                Afstand (km)
              </label>
              <span className="text-lg font-bold text-slate-900">{distance} km</span>
            </div>
            <input
              type="range"
              min="1"
              max="100"
              value={distance}
              onChange={handleDistanceChange}
              className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>1 km</span>
              <span>50 km</span>
              <span>100 km</span>
            </div>
          </div>

          {/* Duration Slider */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400" />
                Estimeret rejsetid (minutter)
              </label>
              <span className="text-lg font-bold text-slate-900">{duration} min</span>
            </div>
            <input
              type="range"
              min="2"
              max="120"
              value={duration}
              onChange={handleDurationChange}
              className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>2 min</span>
              <span>60 min</span>
              <span>120 min</span>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="autoEstimate"
                checked={autoEstimateTime}
                onChange={(e) => setAutoEstimateTime(e.target.checked)}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
              />
              <label htmlFor="autoEstimate" className="text-xs text-slate-500 cursor-pointer select-none">
                Estimer tid automatisk ud fra afstand
              </label>
            </div>
          </div>

          {/* Særlige tillæg */}
          <div className="space-y-2 pt-2">
            <label className="text-sm font-semibold text-slate-700 block">Særlige tillæg</label>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => toggleExtra('cykel')}
                className={`flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                  extras.cykel
                    ? 'border-blue-500 bg-blue-50/30 text-blue-900'
                    : 'border-slate-100 hover:border-slate-200 text-slate-600'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`h-5 w-5 rounded-md border flex items-center justify-center ${
                    extras.cykel ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                  }`}>
                    {extras.cykel && <Check className="h-3 w-3 stroke-[3]" />}
                  </div>
                  <span className="text-xs font-medium">Cykel (+30 kr.)</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => toggleExtra('koerestol')}
                className={`flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                  extras.koerestol
                    ? 'border-blue-500 bg-blue-50/30 text-blue-900'
                    : 'border-slate-100 hover:border-slate-200 text-slate-600'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`h-5 w-5 rounded-md border flex items-center justify-center ${
                    extras.koerestol ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                  }`}>
                    {extras.koerestol && <Check className="h-3 w-3 stroke-[3]" />}
                  </div>
                  <span className="text-xs font-medium">Kørestol (+100 kr.)</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Right: Results Breakdown */}
        <div className="bg-slate-900 text-white p-6 sm:p-8 md:col-span-5 flex flex-col justify-between">
          <div className="space-y-6">
            <div>
              <h4 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Beregnet pris</h4>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                  {totalCost.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-lg font-semibold text-blue-400">kr.</span>
              </div>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                <Info className="h-3 w-3 text-blue-400 shrink-0" />
                Vejledende pris inkl. moms
              </p>
            </div>

            <div className="border-t border-white/10 pt-6 space-y-4">
              <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400">Prisopbygning</h5>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Starttakst</span>
                  <span className="font-medium text-white">
                    {startCost.toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr.
                  </span>
                </div>

                <div className="flex justify-between text-slate-300">
                  <div className="flex flex-col">
                    <span>Kilometertakst</span>
                    <span className="text-xs text-slate-400">
                      {distance} km × {currentRates.km.toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr.
                    </span>
                  </div>
                  <span className="font-medium text-white self-end">
                    {kmCost.toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr.
                  </span>
                </div>

                <div className="flex justify-between text-slate-300">
                  <div className="flex flex-col">
                    <span>Tidstakst</span>
                    <span className="text-xs text-slate-400">
                      {duration} min × {currentRates.min.toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr.
                    </span>
                  </div>
                  <span className="font-medium text-white self-end">
                    {timeCost.toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr.
                  </span>
                </div>

                {extrasCost > 0 && (
                  <div className="flex justify-between text-slate-300">
                    <div className="flex flex-col">
                      <span>Valgte tillæg</span>
                      <span className="text-xs text-slate-400">
                        {[
                          extras.cykel && 'Cykel (30 kr.)',
                          extras.koerestol && 'Kørestol (100 kr.)'
                        ].filter(Boolean).join(', ')}
                      </span>
                    </div>
                    <span className="font-medium text-white self-end">
                      {extrasCost.toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 mt-6 space-y-4">
            <p className="text-xs text-slate-400 leading-relaxed">
              Bemærk, at dette er en vejledende beregning. Den faktiske pris kan variere afhængigt af trafikforhold, ventetid undervejs og det præcise rutevalg.
            </p>
            
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
              className="flex w-full items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-2xl transition-all active:scale-95 text-center"
            >
              <Phone className="h-5 w-5" />
              <span>Ring og bestil: {companyInfo.phone}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
